import React from 'react'

export function renderInputField(props) {
  const {
    input,
    name,
    label,
    type,
    readOnly, // allow this prop
    disabled, // allow this too, in case it's used
    autoComplete,
    placeholder,
    style,
    min,
    max,
    meta: { touched, error }
  } = props;

  return (
    <div className="form-group">
      {label && <label htmlFor={name}>{label}</label>}
      <input
        className={`form-control ${touched && error ? 'is-invalid' : ''}`}
        {...input}
        type={type}
        readOnly={readOnly}
        disabled={disabled}
        autoComplete={autoComplete}
        placeholder={placeholder}
        style={style}
        min={min}
        max={max}
      />
      {touched && error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

export function renderInputLoginField(props) {
  const {
    input,
    name,
    label,
    type,
    meta: { touched, error }
  } = props;

  return (
    <div className="form-group" style={{ marginBottom: '20px', position: 'relative' }}>
      <input
        {...input}
        id={name}
        type={type}
        placeholder={label} // Use label as placeholder inside input
        className={`form-control login-input ${touched && error ? 'is-invalid' : ''}`}
        style={{
          backgroundColor: 'transparent',
          border: '5px solid white',
          borderRadius: '40px',
          color: 'white',
          fontSize: '16px',
          padding: '12px',
          outline: 'none',
          textAlign: 'center'
        }}
      />
      {touched && error && (
        <div style={{
          backgroundColor: 'white',
          color: '#dc3545',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '500',
          marginTop: '8px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}


export function renderTextAreaField(props) {
  const {input, name, label, meta: { touched, error }} = props

  return (
    <div className="form-group">
      { label && <label htmlFor={name}>{label}</label> }
      <textarea className={`form-control ${touched && error && 'is-invalid'}`}
             {...input} />
      { touched && error && <div className="invalid-feedback">{error}</div> }
    </div>
  )
}

// Numeric stepper / counter with - and + buttons. Better than a long button
// row or <select> when there are many sequential numeric values. Props:
// `min` (default 0), `max` (default 15), `step` (default 1).
export function renderCounterField(props) {
  const {
    input,
    label,
    min = 0,
    max = 15,
    step = 1,
    meta: { touched, error }
  } = props;

  const parsed = Number(input.value);
  const value = (input.value === '' || input.value === undefined || input.value === null || Number.isNaN(parsed))
    ? min
    : parsed;
  const invalid = touched && error;

  const setValue = (v) => input.onChange(Math.min(max, Math.max(min, v)));

  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <div className={`counter-field ${invalid ? 'is-invalid-group' : ''}`}>
        <button
          type="button"
          className="counter-btn"
          aria-label="Disminuir"
          disabled={value <= min}
          onClick={() => setValue(value - step)}
        >
          <i className="fas fa-minus" />
        </button>
        <span className="counter-value">{value}</span>
        <button
          type="button"
          className="counter-btn"
          aria-label="Aumentar"
          disabled={value >= max}
          onClick={() => setValue(value + step)}
        >
          <i className="fas fa-plus" />
        </button>
      </div>
      {invalid && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

// Segmented "multibutton" group — a friendlier replacement for a <select>
// when there are only a handful of options. `options` is an array of
// { value, label, icon?, fab? }. Pass `grid` for a wrapping grid layout
// (e.g. many numeric options) or `columns` to force a fixed column count.
export function renderButtonGroupField(props) {
  const {
    input,
    label,
    options = [],
    grid,
    columns,
    meta: { touched, error }
  } = props;

  const current = input.value === undefined || input.value === null ? '' : input.value;
  const invalid = touched && error;

  return (
    <div className="form-group">
      {label && <label>{label}</label>}
      <div
        role="group"
        className={`btn-multi ${(grid || columns) ? 'btn-multi-grid' : ''} ${invalid ? 'is-invalid-group' : ''}`}
        style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
      >
        {options.map((opt) => {
          const active = String(current) === String(opt.value);
          return (
            <button
              key={String(opt.value)}
              type="button"
              aria-pressed={active}
              className={`btn-multi-btn ${active ? 'active' : ''}`}
              onClick={() => input.onChange(opt.value)}
            >
              {opt.icon && <i className={`${opt.fab ? 'fab' : 'fas'} ${opt.icon}`} />}
              {opt.label != null && <span>{opt.label}</span>}
            </button>
          );
        })}
      </div>
      {invalid && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
}

export function renderSelectField(props) {
  const {
    input,
    name,
    label,
    disabled,
    meta: { touched, error },
    children
  } = props;

  return (
    <div className="form-group">
      {label && <label htmlFor={name}>{label}</label>}
      <select
        className={`form-control ${touched && error ? 'is-invalid' : ''}`}
        {...input}
        disabled={disabled}
      >
        {children}
      </select>
      {touched && error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

export function renderColorField(props) {
  const {
    input,
    name,
    label,
    meta: { touched, error }
  } = props;

  // Extract base color (first 6 digits) and alpha (last 2 digits if present)
  const parseColorWithAlpha = (value) => {
    if (!value) return { base: '#000000', alpha: 'ff' };
    
    // Handle 8-digit hex (#RRGGBBAA)
    if (value.startsWith('#') && value.length === 9) {
      return {
        base: value.substring(0, 7),
        alpha: value.substring(7, 9)
      };
    }
    
    // Handle 6-digit hex (#RRGGBB)
    if (value.startsWith('#') && value.length === 7) {
      return {
        base: value,
        alpha: 'ff'
      };
    }
    
    return { base: value || '#000000', alpha: 'ff' };
  };

  const { base, alpha } = parseColorWithAlpha(input.value);
  
  // Convert hex alpha to decimal (0-100)
  const alphaPercent = Math.round((parseInt(alpha, 16) / 255) * 100);

  const handleColorChange = (e) => {
    const newBase = e.target.value;
    const currentAlpha = parseColorWithAlpha(input.value).alpha;
    input.onChange(newBase + currentAlpha);
  };

  const handleAlphaChange = (e) => {
    const percent = parseInt(e.target.value) || 0;
    const hexAlpha = Math.round((percent / 100) * 255).toString(16).padStart(2, '0');
    const currentBase = parseColorWithAlpha(input.value).base;
    input.onChange(currentBase + hexAlpha);
  };

  const handleTextChange = (e) => {
    input.onChange(e.target.value);
  };

  return (
    <div className="form-group">
      {label && <label htmlFor={name}>{label}</label>}
      <div className="d-flex align-items-center mb-2">
        <input
          type="color"
          className="mr-2"
          value={base}
          onChange={handleColorChange}
          style={{ 
            width: '50px', 
            height: '38px', 
            border: '1px solid #ced4da',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        />
        <input
          type="text"
          className={`form-control ${touched && error ? 'is-invalid' : ''}`}
          value={input.value}
          onChange={handleTextChange}
          onBlur={input.onBlur}
          placeholder="#000000 or #000000ff"
          style={{ flex: 1 }}
        />
      </div>
      <div className="d-flex align-items-center">
        <label className="mb-0 mr-2" style={{ fontSize: '12px', minWidth: '60px' }}>
          Opacidad:
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={alphaPercent}
          onChange={handleAlphaChange}
          style={{ flex: 1 }}
          className="mr-2"
        />
        <span style={{ fontSize: '12px', minWidth: '35px' }}>{alphaPercent}%</span>
      </div>
      {touched && error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

export function renderCheckboxField(props) {
  const {
    input,
    name,
    label,
    meta: { touched, error }
  } = props;

  return (
    <div className="form-group">
      {label && <label htmlFor={name}>{label}</label>}
      <div
        onClick={() => input.onChange(!input.value)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: input.value ? '#28a745' : '#dc3545',
          borderRadius: '25px',
          padding: '4px',
          cursor: 'pointer',
          width: '80px',
          height: '32px',
          position: 'relative',
          transition: 'background-color 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
        title={input.value ? 'Click para deshabilitar' : 'Click para habilitar'}
      >
        <div
          style={{
            position: 'absolute',
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            transition: 'transform 0.3s ease',
            transform: input.value ? 'translateX(50px)' : 'translateX(2px)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          }}
        />
        <span
          style={{
            color: 'white',
            fontSize: '11px',
            fontWeight: 'bold',
            position: 'absolute',
            left: input.value ? '8px' : 'auto',
            right: input.value ? 'auto' : '8px',
            userSelect: 'none',
          }}
        >
          {input.value ? 'SÍ' : 'NO'}
        </span>
      </div>
      {touched && error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

export function renderCheckboxPillField(props) {
  const {
    input,
    label,
    icon,
    fab,
    meta: { touched, error }
  } = props;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px 14px', borderRadius: '8px',
      background: input.value ? 'rgba(40,167,69,0.08)' : 'rgba(0,0,0,0.04)',
      border: input.value ? '1px solid rgba(40,167,69,0.3)' : '1px solid rgba(0,0,0,0.1)',
    }}>
      {icon && (
        <i
          className={`${fab ? 'fab' : 'fas'} ${icon}`}
          style={{ color: input.value ? '#28a745' : '#adb5bd', fontSize: '15px', width: '18px', textAlign: 'center', flexShrink: 0 }}
        />
      )}
      <span style={{ fontSize: '13px', fontWeight: 500, color: '#495057', flex: 1 }}>{label}</span>
      <div
        onClick={() => input.onChange(!input.value)}
        style={{
          display: 'inline-flex', alignItems: 'center',
          backgroundColor: input.value ? '#28a745' : '#dc3545',
          borderRadius: '25px', padding: '4px',
          cursor: 'pointer', width: '80px', height: '32px',
          position: 'relative', transition: 'background-color 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)', flexShrink: 0,
        }}
        title={input.value ? 'Click para deshabilitar' : 'Click para habilitar'}
      >
        <div style={{
          position: 'absolute', backgroundColor: 'white', borderRadius: '50%',
          width: '24px', height: '24px', transition: 'transform 0.3s ease',
          transform: input.value ? 'translateX(50px)' : 'translateX(2px)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }} />
        <span style={{
          color: 'white', fontSize: '11px', fontWeight: 'bold',
          position: 'absolute',
          left: input.value ? '8px' : 'auto',
          right: input.value ? 'auto' : '8px',
          userSelect: 'none',
        }}>
          {input.value ? 'SÍ' : 'NO'}
        </span>
      </div>
      {touched && error && <div className="invalid-feedback" style={{ gridColumn: '1/-1' }}>{error}</div>}
    </div>
  );
}
